/**
 * Analytics utility for tracking events via Google Analytics 4
 * Respects user consent settings from cookie banner
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

type EventCategory = 
  | 'video_interaction'
  | 'filter_change'
  | 'navigation'
  | 'engagement'
  | 'share';

interface AnalyticsEvent {
  category: EventCategory;
  action: string;
  label?: string;
  value?: number;
}

/**
 * Track a custom event to Google Analytics 4
 */
export function trackEvent({ category, action, label, value }: AnalyticsEvent): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  // Check if analytics consent was given
  const consent = localStorage.getItem('cookie_consent');
  const analyticsGranted = consent === 'accepted' || 
    (consent === 'custom' && localStorage.getItem('consent_analytics') === 'granted');
  
  if (!analyticsGranted) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
}

// Convenience functions for common events

export function trackVideoPlay(videoId: string, title: string): void {
  trackEvent({
    category: 'video_interaction',
    action: 'play_video',
    label: `${title} (${videoId})`,
  });
}

export function trackVideoShare(videoId: string): void {
  trackEvent({
    category: 'share',
    action: 'share_video',
    label: videoId,
  });
}

export function trackFilterChange(filterType: string, value: string): void {
  trackEvent({
    category: 'filter_change',
    action: `change_${filterType}`,
    label: value,
  });
}

export function trackChannelClick(channelId: string, channelTitle: string): void {
  trackEvent({
    category: 'navigation',
    action: 'view_channel',
    label: `${channelTitle} (${channelId})`,
  });
}

export function trackWatchLaterAdd(videoId: string): void {
  trackEvent({
    category: 'engagement',
    action: 'add_watch_later',
    label: videoId,
  });
}

export function trackRandomVideo(): void {
  trackEvent({
    category: 'engagement',
    action: 'random_video',
  });
}

export function trackShareLink(): void {
  trackEvent({
    category: 'share',
    action: 'copy_link',
  });
}

export function trackHighlightClick(section: string, videoId: string): void {
  trackEvent({
    category: 'engagement',
    action: `click_${section}`,
    label: videoId,
  });
}

